import { api } from 'shared-web';
import type { IntegrationSearchParams } from '../types.ts';

// Integration types (based on mock data structure)
interface IntegrationData {
  id: string;
  title: string;
  description: string;
  icon: any;
  featured: boolean;
  category: string;
  documentation?: string;
  createdAt: string;
  updatedAt: string;
  stats: {
    uses: number;
    rating: number;
    reviews: number;
  };
  configuration: {
    inputs: Array<{
      name: string;
      type: string;
      required: boolean;
      description: string;
      example?: string;
      default?: string;
      options?: string[];
    }>;
    outputs: Array<{
      name: string;
      type: string;
      description: string;
      example?: string;
    }>;
  };
}

export const integrationService = {
  // Get paginated integrations with filtering (from cached JSON)
  list: async (params?: IntegrationSearchParams) => {
    const response = await api.get<any>('/v1/public/integrations.json');

    // The cached endpoint returns data directly, not wrapped in ApiResponse format
    const data = response.data || response;

    // Transform the data to match frontend expectations
    const rawItems = data.data || data || [];

    const transformedItems = rawItems.map((item: any) => ({
      ...item,
      configuration: {
        inputs: Object.entries(item.configuration?.inputs || {}).map(([name, config]: [string, any]) => ({
          name,
          type: Array.isArray(config.type) ? config.type.flat().join('|') : config.type,
          required: config.required || false,
          description: config.description || ''
        })),
        outputs: Object.entries(item.configuration?.outputs || {}).map(([name, config]: [string, any]) => ({
          name,
          type: Array.isArray(config.type) ? config.type.flat().join('|') : config.type,
          description: config.description || ''
        }))
      }
    }));

    // Client-side filtering since it's cached data
    let filteredItems = transformedItems;

    if (params?.search || params?.q) {
      const searchTerm = (params?.search || params?.q || '').toLowerCase();
      filteredItems = filteredItems.filter((item: any) =>
        item.title?.toLowerCase().includes(searchTerm) ||
        item.description?.toLowerCase().includes(searchTerm) ||
        item.category?.toLowerCase().includes(searchTerm)
      );
    }

    if (params?.category && params.category !== 'All') {
      filteredItems = filteredItems.filter((item: any) => item.category === params.category);
    }

    if (params?.featured !== undefined) {
      filteredItems = filteredItems.filter((item: any) => item.featured === params.featured);
    }

    // Client-side pagination
    const page = params?.page || 1;
    const limit = params?.limit || 20;
    const start = (page - 1) * limit;
    const paginatedItems = filteredItems.slice(start, start + limit);

    const result = {
      data: paginatedItems,
      pagination: {
        page,
        limit,
        total: filteredItems.length,
        totalPages: Math.ceil(filteredItems.length / limit)
      },
      success: true
    };

    return result;
  },

  // Get single integration by ID (from cached data)
  getById: async (id: string) => {
    const response = await api.get<any>('/v1/public/integrations.json');
    const data = response.data || response;
    const rawItems = data.data || data || [];
    const rawIntegration = rawItems.find((item: any) => item.id === id);

    if (!rawIntegration) {
      throw new Error(`Integration with id ${id} not found`);
    }

    // Transform the data to match frontend expectations
    const integration = {
      ...rawIntegration,
      configuration: {
        inputs: Object.entries(rawIntegration.configuration?.inputs || {}).map(([name, config]: [string, any]) => ({
          name,
          type: Array.isArray(config.type) ? config.type.flat().join('|') : config.type,
          required: config.required || false,
          description: config.description || ''
        })),
        outputs: Object.entries(rawIntegration.configuration?.outputs || {}).map(([name, config]: [string, any]) => ({
          name,
          type: Array.isArray(config.type) ? config.type.flat().join('|') : config.type,
          description: config.description || ''
        }))
      }
    };

    return integration;
  },

  // Get integration categories (from cached data)
  getCategories: async () => {
    const response = await api.get<any>('/v1/public/integrations.json');
    const data = response.data || response;
    const rawItems = data.data || data || [];

    // Extract unique categories from cached integrations
    const categories = new Set<string>();
    rawItems.forEach((item: any) => {
      if (item.category) {
        categories.add(item.category);
      }
    });

    return Array.from(categories).sort();
  },

  // Get featured integrations (from cached data)
  getFeatured: async (limit?: number) => {
    const response = await api.get<any>('/v1/public/integrations.json');
    const data = response.data || response;
    const rawItems = data.data || data || [];

    // Filter featured integrations
    let featured = rawItems.filter((item: any) => item.featured) || [];

    // Apply limit if provided
    if (limit && limit > 0) {
      featured = featured.slice(0, limit);
    }

    return featured;
  },

  // Install an integration (requires authentication)
  install: async (id: string) => {
    throw new Error('Installing integrations requires authentication. Please log in to the dashboard.');
  },

  // Uninstall an integration (requires authentication)
  uninstall: async (id: string) => {
    throw new Error('Uninstalling integrations requires authentication. Please log in to the dashboard.');
  },
};